import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowRight, Check, X, ChevronDown, Package, Image as ImageIcon,
  FileSpreadsheet, Layers, Wrench, ShieldCheck, Coins, Store, Zap, LifeBuoy, Linkedin
} from 'lucide-react';
import PublicNav from '../components/marketing/PublicNav';
import PublicFooter from '../components/marketing/PublicFooter';
import Seo, { JsonLd } from '../components/Seo';
import { faqSchema } from '../constants/seo';
import {
  HOW_IT_WORKS, FEATURES, STRUGGLE, EDGE, AUDIENCE, FAQS, FOUNDER, SHOWCASES
} from '../content/homeContent';

// ---------------------------------------------------------------------------
// The home page.
//
// Structured like a modern SaaS landing page — hero, what it is, how it works,
// problem/solution, features, who it is for, pricing teaser, FAQ, closing CTA.
//
// The copy lives in content/homeContent.js so the claims can be reviewed on
// their own. Nothing here says a number nobody has counted or names a
// marketplace that is not switched on: the one thing a landing page cannot
// afford is to be the first place a seller catches you out.
// ---------------------------------------------------------------------------

const FEATURE_ICONS = {
  images: ImageIcon,
  copy: Sparkles,
  fields: Layers,
  catalogue: Store,
  multi: Package,
  files: FileSpreadsheet
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-canvas text-ink transition-colors">
      <Seo />
      <JsonLd id="faq-schema" schema={faqSchema(FAQS)} />
      <PublicNav />

      {/* A <main> landmark, which this page did not have. It tells a screen
          reader where the content starts, and tells a search engine which part
          of the document is the page rather than the furniture around it. */}
      <main>
      <Hero />
      <WhatIsIt />
      <HowItWorks />
      <ProblemSolution />
      <Features />
      <Audience />
      <ToolsStrip />
      <PricingTeaser />
      <Founder />
      <Faq />
      <ClosingCta />
      </main>

      <PublicFooter />
    </div>
  );
}

// ── hero ───────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="aurora">
      <div className="max-w-7xl mx-auto px-4 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="brand-chip"><Sparkles size={12} /> Built for Indian marketplace sellers</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight mt-4">
            Ai Generate a product.
            <span className="block brand-text">Download a listing that is ready to upload.</span>
          </h1>
          {/* A DEFINITION FIRST, then the action.
              What an AI assistant quotes when somebody asks "what is
              SellerList AI" is the first sentence under the heading, and a
              sentence that opens mid-action ("Add a product…") answers a
              question nobody asked. One line saying what the thing IS, then
              the line saying what it DOES. */}
          <p className="text-base text-ink-2 mt-5 max-w-lg leading-relaxed">
            <strong className="text-ink">SellerList AI is a product cataloguing tool for Indian
            marketplace sellers.</strong> It writes your product title and description, prepares
            the images, fills in the marketplace fields, and gives you a Meesho-ready upload
            file — from one product, in a few minutes.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/register">
              <span className="inline-flex items-center gap-2 rounded-xl bg-brand-gradient text-white
                               font-semibold px-6 py-3 text-sm shadow-sm hover:shadow-glow transition">
                Start free <ArrowRight size={15} />
              </span>
            </Link>
            <Link to="/pricing">
              <span className="inline-flex items-center gap-2 rounded-xl border border-line bg-surface
                               font-semibold px-6 py-3 text-sm hover:border-brand-500 transition">
                See pricing
              </span>
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-6 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-emerald-500" /> Free credits to start</span>
            <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-emerald-500" /> No card required</span>
            <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-emerald-500" /> We never ask for your marketplace password</span>
          </div>
        </div>

        {/* A representative listing, not a screenshot of a real seller's data.
            Cycles through four real catalogue products — one frozen example
            made a catalogue tool look like it held a single product. */}
        <ShowcaseCard />
      </div>
    </section>
  );
}

const SLIDE_MS = 3000;

function ShowcaseCard() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const go = useCallback((next) => {
    setIndex((i) => (next + SHOWCASES.length) % SHOWCASES.length);
  }, []);

  useEffect(() => {
    // Stopped while the pointer is over the card or the tab is hidden. A
    // carousel that keeps moving under someone reading it is the single most
    // irritating thing a carousel can do.
    if (paused) return undefined;
    timer.current = setInterval(() => {
      if (!document.hidden) setIndex((i) => (i + 1) % SHOWCASES.length);
    }, SLIDE_MS);
    return () => clearInterval(timer.current);
  }, [paused]);

  const item = SHOWCASES[index];

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      className="rounded-2xl border border-line bg-surface shadow-card overflow-hidden
                 transition-shadow duration-300 hover:shadow-glow"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-line bg-surface-2">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-2 text-[11px] text-ink-3">Listing ready · {item.marketplace}</span>
        <span className="ml-auto text-[10px] text-ink-3 tabular-nums">
          {index + 1}/{SHOWCASES.length}
        </span>
      </div>

      <div className="p-5">
        <p className="text-[10px] font-bold tracking-wide text-ink-3">PRODUCT IMAGES</p>
        <div className="grid grid-cols-4 gap-2 mt-2">
          {item.images.map((img, i) => (
            <figure
              key={img.url}
              className="relative aspect-square rounded-lg overflow-hidden bg-surface-2 border border-line"
            >
              <img
                src={img.url}
                alt={`${img.slot} view of ${item.productName}`}
                loading={index === 0 && i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-black/55 text-white
                                     text-[8px] font-medium text-center py-0.5">
                {img.slot}
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="text-[10px] font-bold tracking-wide text-ink-3 mt-5">TITLE</p>
        <p className="text-sm font-medium mt-1 min-h-[2.5em]">{item.productName}</p>

        <p className="text-[10px] font-bold tracking-wide text-ink-3 mt-4">MARKETPLACE FIELDS</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
          {item.fields.map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5 text-[11px] min-w-0">
              <Check size={11} className="text-emerald-500 shrink-0" />
              <span className="text-ink-3 truncate">{k}</span>
              <span className="truncate">{v}</span>
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between mt-5 pt-4 border-t border-line">
          {/* Dots double as controls. Bigger hit area than they look, because
              8px targets are a nuisance on a phone. */}
          <div className="flex items-center gap-1">
            {SHOWCASES.map((sc, i) => (
              <button
                key={sc.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show ${sc.productName}`}
                aria-current={i === index}
                className="p-1.5 -m-0.5 cursor-pointer group"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all duration-300 ${
                    i === index
                      ? 'w-5 bg-brand-500'
                      : 'w-1.5 bg-line group-hover:bg-ink-3'}`}
                />
              </button>
            ))}
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold brand-text">
            <Coins size={12} /> 10 credits
          </span>
        </div>
      </div>
    </div>
  );
}

// ── what is it ─────────────────────────────────────────────────────────────

function WhatIsIt() {
  return (
    <section className="max-w-4xl mx-auto px-4 py-16 text-center">
      <span className="text-[11px] font-bold tracking-widest text-ink-3">OVERVIEW</span>
      <h2 className="text-3xl font-bold mt-2">What is SellerList AI?</h2>
      <p className="text-ink-2 mt-4 leading-relaxed">
        A listing tool for Indian marketplace sellers. You add a product, we prepare the images,
        the Product Name & Description and the category fields the marketplace requires, and you download a file that uploads
        as it is. Nothing is published on your behalf.
      </p>
      <div className="flex flex-wrap justify-center gap-2 mt-6">
        {['Meesho exports live today', 'Files you upload yourself', 'Credits only on completed listings'].map((chip) => (
          <span key={chip} className="inline-flex items-center gap-1.5 rounded-full border border-line
                                      bg-surface px-3 py-1.5 text-xs">
            <ShieldCheck size={12} className="text-emerald-500" /> {chip}
          </span>
        ))}
      </div>
    </section>
  );
}

// ── how it works ───────────────────────────────────────────────────────────

function HowItWorks() {
  return (
    <section id="features" className="border-y border-line bg-surface/50">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="text-[11px] font-bold tracking-widest text-ink-3">HOW IT WORKS</span>
          <h2 className="text-3xl font-bold mt-2">Four steps, and two of them are yours</h2>
          <p className="text-sm text-ink-2 mt-3">
            All of a listing is work nobody should do by hand. This is the part that is left.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.step} className="rounded-2xl border border-line bg-surface p-5">
              <span className="text-2xl font-extrabold brand-text">{s.step}</span>
              <h3 className="font-bold text-sm mt-2">{s.title}</h3>
              <p className="text-xs text-ink-2 mt-1.5 leading-relaxed">{s.blurb}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── problem & solution ─────────────────────────────────────────────────────

function ProblemSolution() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-16">
      <div className="text-center max-w-xl mx-auto mb-10">
        <span className="text-[11px] font-bold tracking-widest text-ink-3">THE PROBLEM</span>
        <h2 className="text-3xl font-bold mt-2">Listing is where the day goes</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6">
          <p className="text-xs font-bold tracking-wide text-rose-500 mb-4">DOING IT BY HAND</p>
          <ul className="space-y-3">
            {STRUGGLE.map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-sm">
                <X size={15} className="text-rose-500 mt-0.5 shrink-0" />
                <span className="text-ink-2">{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-brand-500/40 bg-brand-500/5 p-6">
          <p className="text-xs font-bold tracking-wide brand-text mb-4">WITH SELLERLIST AI</p>
          <ul className="space-y-3">
            {EDGE.map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-sm">
                <Check size={15} className="text-brand-500 mt-0.5 shrink-0" strokeWidth={3} />
                <span className="text-ink-2">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ── features ───────────────────────────────────────────────────────────────

function Features() {
  return (
    <section className="border-y border-line bg-surface/50">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="text-[11px] font-bold tracking-widest text-ink-3">FEATURES</span>
          <h2 className="text-3xl font-bold mt-2">What you actually get</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => {
            const Icon = FEATURE_ICONS[f.key] || Sparkles;
            return (
              <div key={f.key} className="rounded-2xl border border-line bg-surface p-5
                                          hover:border-brand-500/40 transition">
                <span className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center
                                 justify-center mb-3">
                  <Icon size={18} />
                </span>
                <h3 className="font-bold text-sm">{f.title}</h3>
                <p className="text-xs text-ink-2 mt-1.5 leading-relaxed">{f.blurb}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── audience ───────────────────────────────────────────────────────────────

function Audience() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-16">
      <div className="text-center max-w-xl mx-auto mb-10">
        <span className="text-[11px] font-bold tracking-widest text-ink-3">WHO IT IS FOR</span>
        <h2 className="text-3xl font-bold mt-2">Built for the people doing the listing</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {AUDIENCE.map((a) => (
          <div key={a.title} className="rounded-2xl border border-line bg-surface p-5">
            <h3 className="font-bold text-sm">{a.title}</h3>
            <p className="text-xs text-ink-2 mt-1.5 leading-relaxed">{a.blurb}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── tools ──────────────────────────────────────────────────────────────────

function ToolsStrip() {
  const tools = [
    { name: 'Meesho Fee Calculator', blurb: 'Zone shipping, GST and what actually reaches you', free: true },
    { name: 'GST Calculator', blurb: 'Add or strip GST, with the CGST/SGST split', free: true },
    { name: 'Profit Margin Calculator', blurb: 'Margin, markup, ROI and break-even', free: true },
    { name: 'Product Image Generator', blurb: 'Four marketplace-ready images for a catalogue product', free: false }
  ];

  return (
    <section className="border-y border-line bg-surface/50">
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="text-[11px] font-bold tracking-widest text-ink-3">SELLER TOOLS</span>
          <h2 className="text-3xl font-bold mt-2">Usefull Tools that are simply free</h2>
          <p className="text-sm text-ink-2 mt-3">
            Arithmetic on the numbers you enter — no AI, no credits, no sign-up gate on the maths.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tools.map((t) => (
            <div key={t.name} className="rounded-2xl border border-line bg-surface p-5">
              <div className="flex items-start justify-between gap-2">
                <Wrench size={16} className="text-brand-500 shrink-0" />
                <span className={`text-[10px] font-bold rounded-full px-2 py-0.5
                                  ${t.free ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/15 text-amber-600'}`}>
                  {t.free ? 'Free' : '5 credits'}
                </span>
              </div>
              <h3 className="font-bold text-sm mt-3">{t.name}</h3>
              <p className="text-xs text-ink-2 mt-1">{t.blurb}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── pricing teaser ─────────────────────────────────────────────────────────

function PricingTeaser() {
  return (
    <section id="pricing" className="max-w-5xl mx-auto px-4 py-16 text-center">
      <span className="text-[11px] font-bold tracking-widest text-ink-3">PRICING</span>
      <h2 className="text-3xl font-bold mt-2">One price per listing. Nothing hidden.</h2>
      <p className="text-sm text-ink-2 mt-3 max-w-lg mx-auto">
        A completed listing costs <strong className="text-ink">10 credits</strong> — images, title,
        description and every marketplace field. Preparing one and changing your mind costs nothing.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        {[
          { icon: Coins, title: 'Pay per listing', blurb: 'Not per image, not per attempt. Per finished listing.' },
          { icon: Zap, title: 'Preparing is free', blurb: 'Swap an image, a title or a description as often as you like.' },
          { icon: ShieldCheck, title: 'Nothing locked in', blurb: 'Cancel any time. Pack credits never expire.' }
        ].map(({ icon: Icon, title, blurb }) => (
          <div key={title} className="rounded-2xl border border-line bg-surface p-5">
            <Icon size={18} className="text-brand-500 mx-auto" />
            <h3 className="font-bold text-sm mt-3">{title}</h3>
            <p className="text-xs text-ink-2 mt-1">{blurb}</p>
          </div>
        ))}
      </div>

      <Link to="/pricing" className="inline-block mt-8">
        <span className="inline-flex items-center gap-2 rounded-xl bg-brand-gradient text-white
                         font-semibold px-6 py-3 text-sm hover:shadow-glow transition">
          View plans &amp; pricing <ArrowRight size={15} />
        </span>
      </Link>
    </section>
  );
}

// ── faq ────────────────────────────────────────────────────────────────────

// ── founder ────────────────────────────────────────────────────────────────
//
// Placed after the pricing teaser and before the FAQ on purpose: a visitor who
// has just read what this costs is exactly the one asking "and who is behind
// it". No portrait, because there isn't one to use and a stock photo of a
// stranger would be worse than initials.

function Founder() {
  return (
    <section className="border-y border-line bg-surface-2/40">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center">
          <span className="text-[11px] font-bold tracking-widest text-ink-3">WHO IS BEHIND THIS</span>
          <h2 className="text-3xl font-bold mt-2">Founder</h2>
        </div>

        <div className="mt-8 rounded-2xl border border-line bg-surface shadow-card p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <span
              aria-hidden="true"
              className="shrink-0 w-14 h-14 rounded-2xl bg-brand-gradient text-white
                         flex items-center justify-center text-lg font-bold"
            >
              {FOUNDER.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
            </span>
            <div className="min-w-0">
              <p className="font-bold text-lg leading-tight">{FOUNDER.name}</p>
              <p className="text-xs text-ink-3 mt-0.5">{FOUNDER.role}, SellerList AI</p>
              <a
                href={FOUNDER.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                // LinkedIn's own brand blue in light mode, and their dark-mode
                // blue in dark: #0a66c2 on the dark card measured 3.18:1,
                // under the 4.5:1 this size of text needs. #70b5f9 is 8.3:1.
                className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold
                           text-[#0a66c2] dark:text-[#70b5f9] hover:underline"
              >
                <Linkedin size={13} /> Connect on LinkedIn
              </a>
            </div>
          </div>

          <blockquote className="mt-6 space-y-4 text-ink-2 leading-relaxed">
            <p>{FOUNDER.note}</p>
            <p>{FOUNDER.note2}</p>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(null);

  return (
    <section id="faq" className="border-y border-line bg-surface/50">
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <span className="text-[11px] font-bold tracking-widest text-ink-3">FAQ</span>
          <h2 className="text-3xl font-bold mt-2">Frequently asked questions</h2>
        </div>

        <div className="rounded-2xl border border-line bg-surface divide-y divide-line overflow-hidden">
          {FAQS.map((f, i) => (
            <div key={f.q}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
                className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-surface-2 transition"
              >
                <span className="text-sm font-semibold">{f.q}</span>
                <ChevronDown size={16} className={`text-ink-3 shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} />
              </button>
              {open === i && <p className="px-5 pb-4 -mt-1 text-sm text-ink-2 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-ink-3 mt-6 flex items-center justify-center gap-1.5">
          <LifeBuoy size={13} /> Still stuck? Sellers can raise a support ticket from inside the app.
        </p>
      </div>
    </section>
  );
}

// ── closing ────────────────────────────────────────────────────────────────

function ClosingCta() {
  return (
    <section className="max-w-4xl mx-auto px-4 py-20 text-center">
      <span className="brand-chip"><Sparkles size={12} /> Free to start</span>
      <h2 className="text-3xl sm:text-4xl font-extrabold mt-4">
        Stop retyping the same product into the same sheet
      </h2>
      <p className="text-sm text-ink-2 mt-4 max-w-lg mx-auto">
        Create an account, add one product and take it all the way to a downloaded file.
        Your free credits cover it.
      </p>
      <div className="flex flex-wrap justify-center gap-3 mt-7">
        <Link to="/register">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-gradient text-white
                           font-semibold px-8 py-3.5 text-base hover:shadow-glow transition">
            Start free <ArrowRight size={16} />
          </span>
        </Link>
        <Link to="/vendor/register">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface
                           font-semibold px-8 py-3.5 text-base hover:border-brand-500 transition">
            Supply products instead
          </span>
        </Link>
      </div>
    </section>
  );
}
